import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraIconButton, SpecraPanelHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../contexts/authContext';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { useProgram } from '../Programs/hooks';
import { AuditChecklist } from './AuditChecklist';
import { AuditCorrectiveActions } from './AuditCorrectiveActions';
import { useAuditPanel } from './auditPanelStore';
import { AuditStatusBadge, auditorLabel, auditScoreLabel, deriveAuditStatus, periodLabel } from './auditWorkflow';
import { auditQuestionHooks, useAudit, useCompleteAudit } from './hooks';
// The audit IS this panel: a store-backed, portaled, non-modal right panel
// hosting the entire surface — checklist, corrective-action lifecycle, and
// completion. Mounted at DeliveryLayout, it persists while the auditor roams
// the program's pages; there is no separate audit page. Every entry saves as
// it's made, so closing loses nothing and Continue audit reopens mid-stream.
export function AuditChecklistPanel() {
    const { auditId, close } = useAuditPanel();
    const { user } = useAuth();
    const { data: audit, isLoading, isError } = useAudit(auditId ?? '');
    const questions = auditQuestionHooks.useList({
        auditId: auditId ?? ''
    }, {
        enabled: !!auditId
    });
    const complete = useCompleteAudit();
    const [summaryOpen, setSummaryOpen] = useState(false);
    // The store is module-global and outlives a login: close the panel when the
    // signed-in user changes so a previous session's audit doesn't reappear.
    // Only a real id → different real id counts — the initial undefined → id
    // transition during auth hydration must not kill a freshly opened panel.
    const prevUserRef = useRef(user?.id);
    useEffect(()=>{
        if (user?.id && prevUserRef.current && prevUserRef.current !== user.id) close();
        if (user?.id) prevUserRef.current = user.id;
    }, [
        user?.id,
        close
    ]);
    // Keyboard escape hatch for a non-modal surface (no focus trap, so this is
    // the one global affordance). Radix dialogs/selects inside preventDefault
    // their own Escape — an already-handled press must not also close the panel.
    useEffect(()=>{
        if (!auditId) return;
        const onKeyDown = (e)=>{
            if (e.key === 'Escape' && !e.defaultPrevented) close();
        };
        document.addEventListener('keydown', onKeyDown);
        return ()=>document.removeEventListener('keydown', onKeyDown);
    }, [
        auditId,
        close
    ]);
    // The panel persists while the auditor roams other programs' pages, so the
    // header must say WHOSE audit this is — the page behind it can't. The same
    // query resolves manage_self_assessment (SAD-143), which gates Complete
    // (and the CA verify lane) server-side — the Program Lead's authority, not
    // Quality's.
    const programQuery = useProgram(audit?.programId);
    const program = programQuery.data?.program;
    const programName = program?.programNameFull || program?.programNameShort || '';
    const canManageSelfAssessment = programQuery.data?.canManageSelfAssessment ?? false;
    // Panel is mounted at DeliveryLayout (spans program and non-program routes),
    // so it can't use useProgramContext — resolve canEdit off this same
    // program-scoped query instead, same as canManageSelfAssessment above.
    const canEdit = programQuery.data?.canEdit ?? false;
    if (!auditId) return null;
    const items = questions.data?.items ?? [];
    const allReviewed = items.length > 0 && items.every((q)=>q.result !== '');
    // Same terminal-state set as the badge, so a legacy Published/Closed audit
    // is frozen read-only rather than showing Complete over editable controls.
    const isComplete = audit ? deriveAuditStatus(audit).label === 'Complete' : false;
    const readOnly = !canEdit || isComplete;
    // CompleteAudit only accepts these two states server-side; gating on the
    // derived label would offer a doomed action for legacy Draft/In Review.
    const canComplete = [
        'In Progress',
        'Corrective Actions Open'
    ].includes(audit?.state ?? '');
    const unavailable = isError || !isLoading && !audit;
    // Portaled to document.body (the ComplianceDetailDrawer pattern) so the
    // fixed positioning can't be captured by a transformed/filtered ancestor in
    // whatever layout subtree happens to host the panel.
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs("aside", {
        "aria-label": "Program audit panel",
        className: "fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l bg-card shadow-lg",
        "data-testid": Testids.deliveryCompliance.auditPanel,
        // No URL carries the audit id anymore (the audit has no route);
        // automation and support tooling read it off the panel itself.
        "data-audit-id": auditId,
        children: [
            /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                title: `Program Audit${audit ? ` — ${periodLabel(audit)}` : ''}`,
                subtitle: programName || undefined,
                actions: /*#__PURE__*/ _jsx(SpecraIconButton, {
                    size: "sm",
                    variant: "ghost",
                    onClick: close,
                    label: "Close audit panel",
                    "data-testid": Testids.deliveryCompliance.auditPanelClose,
                    children: /*#__PURE__*/ _jsx(X, {
                        className: "size-4"
                    })
                })
            }),
            audit ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-b px-3 py-2 text-xs text-muted-foreground sm:px-4",
                children: [
                    /*#__PURE__*/ _jsx(AuditStatusBadge, {
                        audit: audit
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Auditor: ",
                            auditorLabel(audit)
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "Score: ",
                            auditScoreLabel(audit)
                        ]
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 space-y-6 overflow-y-auto p-4",
                children: unavailable ? // A deleted audit, a stale/foreign-tenant id, OR a caller outside the
                // SAD-143 self-assessment read set all read identically here BY
                // DESIGN: GetAudit now NotFounds anyone who isn't FULL access
                // (compliance.go's hasSelfAssessmentFullAccess) exactly like it
                // already NotFounds a deleted/foreign audit, so this panel can't
                // distinguish "gone" from "not yours to see" — and must not try to,
                // since GetProgramResponse.can_view_self_assessment isn't resolvable
                // here anyway (it needs the audit's own programId, which a denied
                // GetAudit never returns). Either way this must not read as loading
                // forever; Close in the header remains the way out.
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "Audit unavailable — it may have been deleted, belong to another session, or you may not have access to this program's self-assessment. If a correction was assigned directly to you, resolve it from your Dashboard’s “Assigned to you” card instead."
                }) : isLoading || !audit ? /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "Loading audit…"
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        isComplete && audit.auditSummary ? /*#__PURE__*/ _jsxs("div", {
                            className: "rounded-md border bg-muted/50 p-3",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs font-medium text-muted-foreground",
                                    children: "Audit summary"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 whitespace-pre-wrap text-sm",
                                    children: audit.auditSummary
                                })
                            ]
                        }) : null,
                        !canManageSelfAssessment ? /*#__PURE__*/ _jsx("p", {
                            className: "rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground",
                            "data-testid": Testids.deliveryCompliance.auditViewOnlyNotice,
                            children: "You can view this self-assessment but cannot make changes — only someone with self-assessment management access can edit, complete, or send corrections. If a correction below is assigned to you, you can still resolve it."
                        }) : null,
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-sm font-semibold",
                                    children: "Standard checklist"
                                }),
                                /*#__PURE__*/ _jsx(AuditChecklist, {
                                    auditId: audit.id,
                                    programId: audit.programId,
                                    readOnly: readOnly,
                                    canManageSelfAssessment: canManageSelfAssessment
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-sm font-semibold",
                                    children: "Corrections"
                                }),
                                /*#__PURE__*/ _jsx(AuditCorrectiveActions, {
                                    auditId: audit.id,
                                    readOnly: readOnly,
                                    canManageSelfAssessment: canManageSelfAssessment
                                })
                            ]
                        })
                    ]
                })
            }),
            audit && !readOnly && canComplete && canManageSelfAssessment ? /*#__PURE__*/ _jsx("div", {
                className: "border-t p-3",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    className: "w-full",
                    onClick: ()=>setSummaryOpen(true),
                    disabled: !allReviewed || complete.isPending,
                    "data-testid": Testids.deliveryCompliance.auditComplete,
                    children: "Complete Audit"
                })
            }) : null,
            audit ? /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: summaryOpen,
                onOpenChange: setSummaryOpen,
                title: "Complete Audit",
                fields: [
                    {
                        kind: 'textarea',
                        name: 'auditSummary',
                        label: 'Audit summary',
                        rows: 4,
                        helpText: 'Capture findings, improvements, or context not tied to a single item. Completing freezes the score and every correction must already be verified.'
                    }
                ],
                defaultValues: {
                    auditSummary: audit.auditSummary
                },
                onSubmit: async (v)=>{
                    await complete.mutateAsync({
                        auditId: audit.id,
                        auditSummary: v.auditSummary
                    });
                    setSummaryOpen(false);
                },
                isSubmitting: complete.isPending,
                submitLabel: "Complete Audit"
            }) : null
        ]
    }), document.body);
}
